import type { ARExportOptions } from '../types';
import { exportGLBCfn } from './glb-export.cfn';
import type { ExportManager } from '../export-manager.class';
import { AR_SHEET_MESSAGES, type ARLaunchSheet, createARLaunchSheet } from './ar-launch-sheet.cfn';
import { ARStatus, ARTracking } from '../../../../utils/ar-status.enum';

/**
 * Android / WebXR AR through a real `<model-viewer>` element.
 *
 * Why not drive model-viewer's ARRenderer against our own Three.js scene (the
 * old ModelViewerARManager approach)? Since model-viewer 3.x, ModelScene and
 * ARRenderer dereference `element.shadowRoot` and other ModelViewerElement
 * internals, so they only work when the scene belongs to a real
 * `<model-viewer>`. Google Scene Viewer is not an option either: it is a
 * separate app that downloads the model from a public https URL, so a blob:
 * URL minted in this tab is unreachable for it.
 *
 * Flow:
 *   1. Open the full-screen launch sheet immediately (instant feedback).
 *   2. Export the kitchen to a GLB (meters, no floor/sky) and mint a blob URL.
 *   3. Lazy-load @google/model-viewer, mount `<model-viewer ar ar-modes="webxr">`
 *      with the blob as src, and wait for it to parse the model.
 *   4. The user taps "Place in my space". That tap calls activateAR() inside the
 *      gesture, which Chrome's WebXR requestSession() requires. The GLB export
 *      alone can outlive the ~5s transient-activation window, so launching
 *      straight from the canvas button is not reliable.
 *   5. model-viewer runs the immersive-ar session (hit-test placement, its own
 *      DOM overlay with an exit button) and reports ar-status / ar-tracking,
 *      which are forwarded to the ExportManager AR callbacks.
 */

type ModelViewerElementLike = HTMLElement & {
	loaded: boolean;
	canActivateAR: boolean;
	activateAR: () => Promise<void>;
};

type ViewerSession = {
	viewer: ModelViewerElementLike | null;
	blobUrl: string | null;
};

const MODEL_LOAD_TIMEOUT_MS = 60_000;
const AR_CAPABILITY_TIMEOUT_MS = 4_000;
const AR_CAPABILITY_POLL_MS = 100;
const AR_EXPORT_MAX_TEXTURE_SIZE = 2048;
const INCHES_TO_METERS = 0.0254;

let modelViewerReady: Promise<void> | null = null;

/**
 * Lazy-load @google/model-viewer once and wait for the custom element to be
 * defined. Safe to call early (e.g. when the AR button appears) to hide the
 * chunk download behind the user's first interaction.
 */
export const prewarmModelViewerCfn = (): Promise<void> => {
	if (!modelViewerReady) {
		modelViewerReady = import('@google/model-viewer')
			.then(() => customElements.whenDefined('model-viewer'))
			.then(() => {
				// Normalise to Promise<void>; the constructor is not needed here.
			})
			.catch((error: unknown) => {
				modelViewerReady = null;
				throw error;
			});
	}
	return modelViewerReady;
};

const createViewerElement = (src: string): ModelViewerElementLike => {
	const viewer = document.createElement('model-viewer') as ModelViewerElementLike;
	const attributes: Array<[string, string]> = [
		['alt', '3D preview of your outdoor kitchen'],
		['ar', ''],
		['ar-modes', 'webxr'],
		['ar-scale', 'fixed'],
		['ar-placement', 'floor'],
		['xr-environment', ''],
		['camera-controls', ''],
		['touch-action', 'none'],
		['interaction-prompt', 'none'],
		['shadow-intensity', '1'],
		['environment-image', 'neutral'],
		['exposure', '1'],
		['loading', 'eager'],
		['reveal', 'auto'],
		['src', src]
	];
	attributes.forEach(([name, value]) => viewer.setAttribute(name, value));

	// Replace model-viewer's built-in AR button and progress bar with empty
	// slots: the launch sheet owns the call to action and the loading state.
	['ar-button', 'progress-bar'].forEach((slotName) => {
		const placeholder = document.createElement('div');
		placeholder.slot = slotName;
		placeholder.style.display = 'none';
		viewer.appendChild(placeholder);
	});

	return viewer;
};

const describeLoadError = (event: Event): Error => {
	const { detail } = event as CustomEvent<{ sourceError?: { message?: string } }>;
	const reason = detail?.sourceError?.message;
	return new Error(reason ? `The AR preview could not load the kitchen model: ${reason}` : 'The AR preview could not load the kitchen model.');
};

const waitForModelLoad = (viewer: ModelViewerElementLike, timeoutMs: number): Promise<void> => new Promise<void>((resolve, reject) => {
	if (viewer.loaded) {
		resolve();
		return;
	}
	const abort = new AbortController();
	let timer: ReturnType<typeof setTimeout> | null = null;
	const finish = (error: Error | null): void => {
		abort.abort();
		if (timer !== null) clearTimeout(timer);
		if (error) {
			reject(error);
			return;
		}
		resolve();
	};
	viewer.addEventListener('load', () => finish(null), { signal: abort.signal });
	viewer.addEventListener('error', (event: Event) => finish(describeLoadError(event)), { signal: abort.signal });
	timer = setTimeout(() => finish(new Error('Timed out while loading the AR preview.')), timeoutMs);
});

/**
 * model-viewer picks its AR mode asynchronously after `src` is set (it awaits
 * navigator.xr.isSessionSupported). Poll `canActivateAR` briefly instead of
 * trusting the first value.
 */
const waitForARCapability = (viewer: ModelViewerElementLike, timeoutMs: number): Promise<boolean> => new Promise<boolean>((resolve) => {
	const startedAt = Date.now();
	const check = (): void => {
		if (viewer.canActivateAR) {
			resolve(true);
			return;
		}
		if (Date.now() - startedAt >= timeoutMs) {
			resolve(false);
			return;
		}
		setTimeout(check, AR_CAPABILITY_POLL_MS);
	};
	check();
});

const wireViewerEvents = (self: ExportManager, viewer: ModelViewerElementLike, sheet: ARLaunchSheet): void => {
	sheet.onLaunch(() => {
		if (!viewer.canActivateAR) {
			sheet.showReady(false);
			return;
		}
		sheet.setBusy(true);
		// Called synchronously inside the tap handler on purpose: Chrome only
		// grants an immersive-ar session while the user activation is fresh.
		viewer.activateAR().catch((error: unknown) => {
			console.warn('[AR] model-viewer activateAR failed:', error);
			sheet.setBusy(false);
			sheet.showError(AR_SHEET_MESSAGES.failed);
		});
	});

	viewer.addEventListener('ar-status', (event: Event) => {
		const { detail } = event as CustomEvent<{ status?: string }>;
		const status = (detail?.status ?? ARStatus.FAILED) as ARStatus;
		self.arCallbacksAccess.onStatusChange?.(status);
		if (status === ARStatus.FAILED) {
			sheet.setBusy(false);
			sheet.showError(AR_SHEET_MESSAGES.failed);
			return;
		}
		if (status === ARStatus.NOT_PRESENTING) {
			sheet.setBusy(false);
			sheet.showHint(AR_SHEET_MESSAGES.ready);
		}
	});

	viewer.addEventListener('ar-tracking', (event: Event) => {
		const { detail } = event as CustomEvent<{ status?: string }>;
		const tracking = detail?.status === ARTracking.TRACKING ? ARTracking.TRACKING : ARTracking.NOT_TRACKING;
		self.arCallbacksAccess.onTrackingChange?.(tracking);
	});
};

const assertKitchenReady = (self: ExportManager): void => {
	const { scene, islandHolder } = self.configAccess;
	if (!scene || !islandHolder) {
		throw new Error('Scene or island holder not available. The kitchen may still be loading.');
	}
	if (islandHolder.children.length === 0) {
		throw new Error('Island holder is empty. Kitchen model is not ready yet.');
	}
};

/** Export the kitchen for AR while the model-viewer chunk loads in parallel. */
const exportKitchenForAR = async (self: ExportManager, includeFloor: boolean): Promise<ArrayBuffer> => {
	const [buffer] = await Promise.all([
		exportGLBCfn(self, {
			skipDownload: true,
			includeFloor,
			includeSky: false,
			scale: INCHES_TO_METERS,
			maxTextureSize: AR_EXPORT_MAX_TEXTURE_SIZE,
			isolateMaterials: true
		}),
		prewarmModelViewerCfn()
	]);
	if (!buffer) throw new Error('GLB export produced no data.');
	return buffer;
};

const releaseViewerSession = (session: ViewerSession): void => {
	session.viewer?.remove();
	session.viewer = null;
	if (session.blobUrl) {
		URL.revokeObjectURL(session.blobUrl);
		session.blobUrl = null;
	}
};

type PresentViewerParams = {
	self: ExportManager;
	sheet: ARLaunchSheet;
	session: ViewerSession;
	buffer: ArrayBuffer;
};

const presentViewer = async ({ self, sheet, session, buffer }: PresentViewerParams): Promise<void> => {
	session.blobUrl = URL.createObjectURL(new Blob([buffer], { type: 'model/gltf-binary' }));
	const viewer = createViewerElement(session.blobUrl);
	session.viewer = viewer;
	wireViewerEvents(self, viewer, sheet);
	sheet.mountViewer(viewer);
	sheet.showPreparing(AR_SHEET_MESSAGES.loadingPreview);

	await waitForModelLoad(viewer, MODEL_LOAD_TIMEOUT_MS);
	if (!sheet.handle.isOpen()) return;

	const canActivateAR = await waitForARCapability(viewer, AR_CAPABILITY_TIMEOUT_MS);
	if (!sheet.handle.isOpen()) return;
	sheet.showReady(canActivateAR);
};

/**
 * Open the WebXR launch sheet for the current kitchen. Resolves once the
 * preview is loaded and the sheet is interactive; rejects (and closes the
 * sheet) if the export or the preview load fails. Failures after that point —
 * a denied camera permission, a missing ARCore install — are reported inside
 * the sheet, which stays open so the user can retry or close it.
 */
export const openModelViewerARCfn = async (
	self: ExportManager,
	options: ARExportOptions = {}
): Promise<void> => {
	const { includeFloor = false } = options;
	assertKitchenReady(self);

	// Only one sheet at a time.
	self.arLaunchSheetAccess?.close();

	const session: ViewerSession = { viewer: null, blobUrl: null };
	const sheet = createARLaunchSheet(() => {
		releaseViewerSession(session);
		if (self.arLaunchSheetAccess === sheet.handle) {
			self.arLaunchSheetAccess = null;
		}
	});
	self.arLaunchSheetAccess = sheet.handle;
	sheet.showPreparing(AR_SHEET_MESSAGES.preparing);

	try {
		const buffer = await exportKitchenForAR(self, includeFloor);
		if (!sheet.handle.isOpen()) return;
		await presentViewer({ self, sheet, session, buffer });
	} catch (error) {
		sheet.handle.close();
		throw error;
	}
};
